import BaseDatafeed from './BaseDatafeed';
import Logger from '../../../utils/Logger';

const CONFIG = {
  INITIAL_RECONNECT_DELAY: 1000,
  MAX_RECONNECT_DELAY: 30000,
  MAX_RECONNECT_ATTEMPTS: 10,
  HEARTBEAT_INTERVAL: 30000,
  HEARTBEAT_TIMEOUT: 5000,
  API_TIMEOUT: 30000,
  API_MAX_RETRIES: 3,
};

/**
 * FuturesDatafeed - PI42 Futures Market Implementation
 * 
 * Handles Futures trading with PI42 API using:
 * - REST API: POST /v1/market/klines
 * - Standard WebSocket for real-time updates
 * - Symbol format: BTCINR (API & Socket)
 */
class FuturesDatafeed extends BaseDatafeed {
  constructor(config) {
    super({
      ...config,
      exchange: 'Unocoin',
      timezone: config.timezone || 'Asia/Kolkata',
    });
    
    // Throw error if environment variables are missing (no hardcoded fallbacks)
    if (!process.env.REACT_APP_API_URL || !process.env.REACT_APP_FUTURES_SOCKET_URL) {
      throw new Error('Missing required environment variables: REACT_APP_API_URL and/or REACT_APP_FUTURES_SOCKET_URL');
    }
    
    this.apiUrl = process.env.REACT_APP_API_URL;
    this.socketUrl = process.env.REACT_APP_FUTURES_SOCKET_URL;
    this.supportedResolutions = ['1', '3', '5', '15', '30', '60', '120', '240', '360', '480', '720', '1D', '1W', '1M'];
    
    // Reconnection state management
    this.reconnectAttempts = 0;
    this.reconnectDelay = CONFIG.INITIAL_RECONNECT_DELAY;
    this.reconnectTimeoutId = null;
    this.heartbeatIntervalId = null;
    this.heartbeatTimeoutId = null;
    this.pendingSubscriptions = new Set();
  }

  /**
   * Initialize standard WebSocket connection for Futures market
   */
  initializeSocket() {
    if (this.socket) return;

    if (this.reconnectAttempts >= CONFIG.MAX_RECONNECT_ATTEMPTS) {
      Logger.error(`[Futures] Max reconnection attempts (${CONFIG.MAX_RECONNECT_ATTEMPTS}) reached. Giving up.`);
      return;
    }

    Logger.info(`[Futures] Initializing WebSocket connection (attempt ${this.reconnectAttempts + 1}/${CONFIG.MAX_RECONNECT_ATTEMPTS}):`, this.socketUrl);
    
    
    this.socket = new WebSocket(this.socketUrl);

    this.socket.onopen = () => {
      Logger.info('[Futures] WebSocket connected successfully!');
      
      // Reset reconnection state on successful connection
      this.reconnectAttempts = 0;
      this.reconnectDelay = CONFIG.INITIAL_RECONNECT_DELAY;
      
      // Start heartbeat
      this.startHeartbeat();
      
      // Process any pending subscriptions
      if (this.pendingSubscriptions.size > 0) {
        Logger.info(`[Futures] Processing ${this.pendingSubscriptions.size} pending subscriptions`);
        this.pendingSubscriptions.forEach(channel => {
          this.subscribeToSocket(channel);
        });
        this.pendingSubscriptions.clear();
      }
      
      // Re-subscribe to all active channels
      Object.values(this.subscribers).forEach(sub => {
        this.subscribeToSocket(sub.channelString);
      });
    };

    this.socket.onerror = (error) => {
      Logger.error('[Futures] WebSocket error:', error);
    };

    this.socket.onclose = (event) => {
      this.stopHeartbeat();
      Logger.warn('[Futures] WebSocket closed. Code:', event.code, 'Reason:', event.reason || 'Unknown');
      
      // Clear any existing reconnect timeout
      if (this.reconnectTimeoutId) {
        clearTimeout(this.reconnectTimeoutId);
        this.reconnectTimeoutId = null;
      }
      
      // Only attempt reconnection if we have active subscribers
      if (Object.keys(this.subscribers).length > 0) {
        this.reconnectAttempts++;
        
        // Add jitter (0-500ms randomness) to prevent thundering herd
        // When 1000 users reconnect simultaneously, this staggers the load
        const jitter = Math.random() * 500;
        const delayWithJitter = this.reconnectDelay + jitter;
        const delaySeconds = (delayWithJitter / 1000).toFixed(1);
        
        Logger.info(`[Futures] Reconnecting in ${delaySeconds}s... (attempt ${this.reconnectAttempts}/${CONFIG.MAX_RECONNECT_ATTEMPTS})`);
        
        this.reconnectTimeoutId = setTimeout(() => {
          this.socket = null;
          this.reconnectDelay = Math.min(this.reconnectDelay * 2, CONFIG.MAX_RECONNECT_DELAY);
          this.initializeSocket();
        }, delayWithJitter);
      } else {
        Logger.info('[Futures] No active subscribers, skipping reconnection');
      }
    };

    this.socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        // Filter out connection status messages
        if (message.status || message.success) {
          Logger.debug('[Futures] Server status message:', message);
          return;
        }
        
        // Extract kline data to detect symbol from price
        const kline = message.k || message.data?.k;
        let detectedSymbol = null;
        
        if (kline && kline.c) {
          const closePrice = parseFloat(kline.c);
          // Detect coin based on price range (rough heuristic)
          if (closePrice > 1000000) {
            detectedSymbol = 'BTCINR';
          } else if (closePrice > 100 && closePrice < 300) {
            detectedSymbol = 'SOLINR or similar';
          } else if (closePrice > 100 && closePrice < 200) {
            detectedSymbol = 'Unknown ~' + Math.floor(closePrice);
          }
        }
        
        Logger.info('[Futures] WebSocket message:', {
          channel: message.channel || 'N/A',
          ps: message.ps || 'N/A',
          price: kline?.c || 'N/A',
          detectedSymbol: detectedSymbol || 'Unknown',
          activeChannel: this.activeChannel
        });
        
        // Handle kline events (proxy relays Pi42 socket.io format)
        if (message.e === 'continuous_kline' && message.k) {
          const channelString = `${message.ps.toLowerCase()}@kline_${message.k.i}`;
          Logger.debug('[Futures] Routing direct format to:', channelString);
          this.handleSocketMessage(message, channelString);
        } else if (message.channel && message.data) {
          // Alternative format: {channel: "btcinr@kline_1m", data: {...}}
          Logger.info(`[Futures] Routing to channel: ${message.channel} (expected: ${this.activeChannel})`);
          this.handleSocketMessage(message.data, message.channel);
        } else {
          Logger.warn('[Futures] Unknown message format:', message);
        }
      } catch (error) {
        Logger.error('[Futures] Error parsing WebSocket message:', error);
      }
    };
  }

  /**
   * Format symbol for PI42 API: "BTCINR" (no separator)
   */
  formatSymbolForAPI(baseCoin, cryptoCoin) {
    return `${cryptoCoin}${baseCoin}`.toUpperCase();
  }

  /**
   * Format stream for WebSocket: "btcinr@kline_1m"
   */
  formatSymbolForSocket(baseCoin, cryptoCoin, resolution) {
    const symbol = this.formatSymbolForAPI(baseCoin, cryptoCoin).toLowerCase();
    const interval = this.mapResolutionToInterval(resolution);
    const channel = `${symbol}@kline_${interval}`;
    Logger.debug(`[Futures] formatSymbolForSocket: ${baseCoin}/${cryptoCoin}, resolution: ${resolution} → channel: ${channel}`);
    return channel;
  }

  /**
   * Map TradingView resolution to PI42 interval format
   */
  mapResolutionToInterval(resolution) {
    const intervalMap = {
      '1': '1m',
      '3': '3m',
      '5': '5m',
      '15': '15m',
      '30': '30m',
      '60': '1h',
      '120': '2h',
      '240': '4h',
      '360': '6h',
      '480': '8h',
      '720': '12h',
      'D': '1d',
      '1D': '1d',
      'W': '1w',
      '1W': '1w',
      'M': '1M',
      '1M': '1M',
    };
    return intervalMap[resolution] || '1m';
  }

  /**
   * Fetch historical bars from PI42 API
   */
  async fetchHistoricalBars(symbolInfo, resolution, periodParams) {
    const { from, to } = periodParams;
    const [baseCoin, cryptoCoin] = symbolInfo.ticker.split('/');
    const pair = this.formatSymbolForAPI(baseCoin, cryptoCoin);
    const interval = this.mapResolutionToInterval(resolution);

    const url = `${this.apiUrl}v1/futures/market/klines`;
    
    // Cap the request to current time to avoid requesting future data
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    const cappedTo = Math.min(to, now);
    const cappedFrom = Math.min(from, now);
    
    // PI42 uses milliseconds for timestamps
    const requestBody = {
      pair: pair,
      interval: interval,
      startTime: cappedFrom * 1000, // Convert to milliseconds
      endTime: cappedTo * 1000,     // Convert to milliseconds
      limit: 1500                   // Max limit
    };

    Logger.info(`[Futures] Fetching bars from ${new Date(from * 1000).toISOString()} to ${new Date(to * 1000).toISOString()}`);
    Logger.info(`[Futures] Timestamps - from: ${from}, to: ${to} (in seconds)`);
    Logger.info(`[Futures] API URL: ${url}`);
    Logger.info(`[Futures] Request body:`, JSON.stringify(requestBody));
    Logger.info(`[Futures] Datafeed timezone:`, this.config.timezone);
    Logger.info(`[Futures] Final timezone:`, this.config.timezone || 'Asia/Kolkata');


    try {
      // Add timeout to prevent indefinite hangs
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), CONFIG.API_TIMEOUT);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      

      Logger.info(`[Futures] API response type: ${typeof data}, isArray: ${Array.isArray(data)}, length: ${Array.isArray(data) ? data.length : 'N/A'}`);
      Logger.info('[Futures] First raw candle from API:', data);
      
      // Log raw response for debugging
      if (!Array.isArray(data)) {
        Logger.error('[Futures] Unexpected response format:', data);
      } else if (data.length === 0) {
        Logger.warn('[Futures] API returned empty array for time range');
      }

      // PI42 returns array directly (not wrapped in object)
      if (!Array.isArray(data) || data.length === 0) {
        return [];
      }

      // Log first raw candle from API to verify timestamps
      if (data.length > 0) {
        Logger.info('[Futures] First raw candle from API:', data[0]);
        Logger.info('[Futures] First candle startTime:', data[0].startTime, '→', new Date(Number.parseInt(data[0].startTime)).toISOString());
      }

      // Transform PI42 response to TradingView bars format
      // Note: PI42 returns timestamps as STRINGS in milliseconds
      const bars = data.map(candle => ({
        time: parseInt(candle.startTime), // Already in milliseconds, just parse to int
        open: parseFloat(candle.open),
        high: parseFloat(candle.high),
        low: parseFloat(candle.low),
        close: parseFloat(candle.close),
        volume: parseFloat(candle.volume)
      }));

      // Sort by time ascending (PI42 returns descending by endTime)
      bars.sort((a, b) => a.time - b.time);

      Logger.info(`[Futures] Returning ${bars.length} bars`);
      
      return bars;

    } catch (error) {
      if (error.name === 'AbortError') {
        Logger.error('[Futures] API request timeout after', CONFIG.API_TIMEOUT / 1000, 'seconds');
      } else {
        Logger.error('[Futures] fetchHistoricalBars error:', error);
      }
      throw error;
    }
  }

  /**
   * Parse WebSocket message for real-time kline updates
   */
  parseSocketMessage(message, channelString = null) {
    try {
      if (!message || !message.k) {
        Logger.warn('[Futures] parseSocketMessage: No kline data in message:', message);
        return null;
      }

      const kline = message.k;

      // Extract symbol from message.ps field or channel name
      let symbol = null;
      if (message.ps) {
        symbol = message.ps.toUpperCase();
      } else if (channelString) {
        // Extract from channel like "btcinr@kline_4h" -> "BTCINR"
        const match = channelString.match(/^([a-z]+)@kline/i);
        if (match) {
          symbol = match[1].toUpperCase();
        }
      }

      // PI42 sends timestamps in milliseconds
      const bar = {
        time: kline.t, // Already in milliseconds
        open: parseFloat(kline.o),
        high: parseFloat(kline.h),
        low: parseFloat(kline.l),
        close: parseFloat(kline.c),
        volume: parseFloat(kline.v),
        symbol: symbol // Add symbol for validation in handleSocketMessage
      };

      // Validate bar data
      if (isNaN(bar.time) || isNaN(bar.close)) {
        Logger.warn('[Futures] Invalid bar data:', kline);
        return null;
      }

      // Log updates at debug level to reduce production noise
      const status = kline.x === true ? 'CLOSED' : 'LIVE';
      Logger.debug(`[Futures] ${status} kline update: ${new Date(bar.time).toLocaleTimeString()} | Symbol: ${symbol || 'N/A'} | O:${bar.open} H:${bar.high} L:${bar.low} C:${bar.close} V:${bar.volume}`);

      return bar;

    } catch (error) {
      Logger.error('[Futures] parseSocketMessage error:', error);
      return null;
    }
  }

  /**
   * Subscribe to WebSocket stream
   */
  subscribeToSocket(channelString) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      Logger.warn('[Futures] WebSocket not connected, queuing subscription:', channelString);
      this.pendingSubscriptions.add(channelString);
      return;
    }

    Logger.info('[Futures] Subscribing to stream:', channelString);
    
    // PI42 Proxy WebSocket subscription format
    const subscribeMessage = {
      action: 'subscribe',
      channel: channelString
    };
    
    this.socket.send(JSON.stringify(subscribeMessage));
  }

  /**
   * Unsubscribe from WebSocket stream
   */
  unsubscribeFromSocket(channelString) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;

    Logger.info('[Futures] Unsubscribing from stream:', channelString);
    
    const unsubscribeMessage = {
      action: 'unsubscribe',
      channel: channelString
    };
    
    this.socket.send(JSON.stringify(unsubscribeMessage));
  }
  
  /**
   * Start WebSocket heartbeat to detect stale connections
   */
  startHeartbeat() {
    this.stopHeartbeat(); // Clear any existing heartbeat
    
    this.heartbeatIntervalId = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        Logger.debug('[Futures] Sending heartbeat ping');
        
        // Set timeout to check for pong response
        this.heartbeatTimeoutId = setTimeout(() => {
          Logger.warn('[Futures] Heartbeat timeout - no pong received. Closing connection.');
          if (this.socket) {
            this.socket.close();
          }
        }, CONFIG.HEARTBEAT_TIMEOUT);
        
        // Send ping (if server supports it, otherwise just check connection state)
        try {
          this.socket.send(JSON.stringify({ action: 'ping' }));
        } catch (error) {
          Logger.error('[Futures] Failed to send heartbeat:', error);
        }
      }
    }, CONFIG.HEARTBEAT_INTERVAL);
  }
  
  /**
   * Stop WebSocket heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatIntervalId) {
      clearInterval(this.heartbeatIntervalId);
      this.heartbeatIntervalId = null;
    }
    if (this.heartbeatTimeoutId) {
      clearTimeout(this.heartbeatTimeoutId);
      this.heartbeatTimeoutId = null;
    }
  }
  
  /**
   * Clean up resources
   */
  destroy() {
    Logger.info('[Futures] Destroying datafeed and cleaning up resources');
    
    // Stop heartbeat
    this.stopHeartbeat();
    
    // Clear reconnection timeout
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    
    // Close socket
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    
    // Clear pending subscriptions
    this.pendingSubscriptions.clear();
    
    // Call parent cleanup if exists
    if (super.destroy) {
      super.destroy();
    }
  }
}

export default FuturesDatafeed;
